import { oc } from "@orpc/contract";
import {
  EMAIL_SEND_CATEGORIES,
  GRAPH_INLINE_ATTACHMENT_MAX_BYTES,
  inlineAttachmentLimitMessage,
} from "@erp/shared-types";
import { z } from "zod";

/**
 * Which org-level default sender / default Cc applies: "orders" (sales-order
 * replies + approvals), "purchase_orders" (PO approvals), "discounts"
 * (discount approval + group replies), or "general" (everything else — NOT a
 * fallback for the specific categories).
 */
export const EmailSendCategorySchema = z.enum(EMAIL_SEND_CATEGORIES);

/**
 * Graph's inline-attachment path caps the total payload at
 * GRAPH_INLINE_ATTACHMENT_MAX_BYTES of raw bytes; base64 is 4 chars per 3
 * bytes. The server re-checks the decoded total across base64 files AND
 * stored documents, so this only bounds the request body.
 */
export const MAX_ATTACHMENT_BASE64_CHARS = Math.ceil(
  (GRAPH_INLINE_ATTACHMENT_MAX_BYTES * 4) / 3,
);

/**
 * Sanity bound on attachment counts. Bytes are what Graph cares about (see
 * above); inbound emails forwarded from the Inbound Emails page carry dozens
 * of files, so the count must never be the thing that blocks a send.
 */
const MAX_ATTACHMENT_COUNT = 100;

export const ComposeReplyAttachmentSchema = z.object({
  name: z.string().min(1).max(255),
  contentType: z.string().min(1).max(255),
  /** Raw file bytes, base64-encoded. */
  contentBytes: z.string().min(1),
});

export const SendComposeReplyInputSchema = z.object({
  /** Sales-order draft the reply is composed from (for threading + sender). */
  salesOrderDraftId: z.string().uuid().optional(),
  to: z.array(z.string().email()).min(1),
  cc: z.array(z.string().email()).optional(),
  bcc: z.array(z.string().email()).optional(),
  subject: z.string().min(1),
  htmlContent: z.string().min(1),
  /** Explicit Send-From mailbox override (authorized server-side). */
  mailboxUpn: z.string().email().optional(),
  /** Files to attach (e.g. the approved discount form). */
  attachments: z
    .array(ComposeReplyAttachmentSchema)
    .max(MAX_ATTACHMENT_COUNT)
    .optional()
    .refine(
      (attachments) =>
        !attachments ||
        attachments.reduce((sum, a) => sum + a.contentBytes.length, 0) <=
          MAX_ATTACHMENT_BASE64_CHARS,
      { message: inlineAttachmentLimitMessage() },
    ),
  /**
   * Inbound email being forwarded. Required when `attachmentDocumentIds` is
   * set — the documents are validated against this email's attachments (org
   * scoping), and it defaults the sending mailbox to the receiving one.
   */
  sourceInboundEmailId: z.string().uuid().optional(),
  /**
   * The source email's documents to attach by id (forwarding) — resolved and
   * size-guarded server-side, so no base64 has to round-trip the browser.
   */
  attachmentDocumentIds: z
    .array(z.string().uuid())
    .max(MAX_ATTACHMENT_COUNT)
    .optional(),
});

export type ComposeReplyAttachment = z.infer<
  typeof ComposeReplyAttachmentSchema
>;

export const SendComposeReplyOutputSchema = z.object({
  outboundEmailId: z.string().uuid(),
  messageId: z.string().nullable(),
  conversationId: z.string().optional(),
});

export const sendComposeReplyContract = oc
  .input(SendComposeReplyInputSchema)
  .output(SendComposeReplyOutputSchema)
  .route({
    method: "POST",
    summary: "Send an email reply",
    description:
      "Sends a composed email (to, cc, bcc, subject, HTML body, attachments) from an authorized mailbox, optionally threaded to a sales order draft or forwarding an inbound email's documents.",
  });

// Mailboxes the current user may send from (org ingest mailboxes), plus the
// org's configured default send UPN for the requested category (if it's among
// them). Empty when the user lacks the SharedMailboxSend permission.
export const SendableMailboxesInputSchema = z.object({
  /** Which category's default UPN to surface; server defaults to "general". */
  category: EmailSendCategorySchema.optional(),
});

export const SendableMailboxesOutputSchema = z.object({
  mailboxes: z.array(
    z.object({ upn: z.string(), name: z.string().nullable() }),
  ),
  defaultUpn: z.string().nullable(),
});

export const sendableMailboxesContract = oc
  .input(SendableMailboxesInputSchema)
  .output(SendableMailboxesOutputSchema)
  .route({
    method: "GET",
    summary: "List mailboxes I can send from",
    description:
      "Lists the org's shared mailboxes the current user may send email from, plus the org default sender for a category; empty without shared-mailbox send permission.",
  });

const DefaultSendMailboxSchema = z
  .object({
    mailboxId: z.string().uuid(),
    upn: z.string(),
    name: z.string().nullable(),
  })
  .nullable();

// All categories at once — the settings page renders them side by side.
export const DefaultSendByCategorySchema = z.object({
  general: DefaultSendMailboxSchema,
  orders: DefaultSendMailboxSchema,
  purchase_orders: DefaultSendMailboxSchema,
  discounts: DefaultSendMailboxSchema,
});

export const getDefaultSendContract = oc
  .output(DefaultSendByCategorySchema)
  .route({
    method: "GET",
    summary: "Get default sending mailboxes",
    description:
      "Returns the org's default sending mailbox for each email category: general, orders, purchase orders and discounts.",
  });

export const SetDefaultSendInputSchema = z.object({
  category: EmailSendCategorySchema,
  emailIngestMailboxId: z.string().uuid().nullable(),
});

export const setDefaultSendContract = oc
  .input(SetDefaultSendInputSchema)
  .output(DefaultSendMailboxSchema)
  .route({
    method: "POST",
    summary: "Set default sending mailbox",
    description:
      "Sets or clears the org's default sending mailbox for one email category (general, orders, purchase orders or discounts). Requires org settings update permission.",
  });

export const composeReplyContracts = {
  send: sendComposeReplyContract,
  sendableMailboxes: sendableMailboxesContract,
  defaultSend: {
    get: getDefaultSendContract,
    set: setDefaultSendContract,
  },
} as const;

export type SendComposeReplyInput = z.infer<typeof SendComposeReplyInputSchema>;
export type SendComposeReplyOutput = z.infer<
  typeof SendComposeReplyOutputSchema
>;
export type SendableMailboxesOutput = z.infer<
  typeof SendableMailboxesOutputSchema
>;
export type DefaultSendByCategory = z.infer<typeof DefaultSendByCategorySchema>;
export type SetDefaultSendInput = z.infer<typeof SetDefaultSendInputSchema>;
